/**
 * Calls the query classification endpoint and selects the class with highest confidence.
 * @param {string} queryText The user's search query.
 * @returns {Promise<string>} The predicted class name (e.g., 'default', 'legal_question') based on confidence threshold.
 */
export const classifyQuery = async (queryText) => {
  const endpointUrl = import.meta.env.VITE_CLASSIFICATION_ENDPOINT_URL;

  if (!endpointUrl) {
    return 'default'; // Default fallback if URL is missing
  }

  if (!queryText || queryText.trim() === "") {
    return 'default';
  }

  try {
    const response = await fetch(endpointUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text: queryText }),
    });

    if (!response.ok) {
      try {
        const errorBody = await response.text();
        console.error("[classifyQuery] API Error Body:", errorBody);
      } catch (e) {
        console.error("[classifyQuery] Could not read error body.");
      }
      return 'default'; // Fallback to default if API fails
    }

    const data = await response.json();

    // Check if we have a valid response
    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
      return 'default';
    }

    // Get the first item (highest confidence class)
    const topClassEntry = Object.entries(data)[0];
    if (!topClassEntry) {
      return 'default';
    }

    const [topClass, topScore] = topClassEntry;

    // Return top class if confidence is above threshold, otherwise default
    if (topScore >= 0.8) {
      return topClass;
    } else {
      return 'default';
    }

  } catch (error) {
    return 'default'; // Fallback to default on network or other errors
  }
};